import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gavel, Clock, AlertTriangle, Shield } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function CreateAuctionPage() {
  const navigate = useNavigate();
  const { user, isSeller } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    listing_id: '',
    title: '',
    description: '',
    start_price: '',
    reserve_price: '',
    bid_increment_min: '50',
    start_time: '',
    end_time: '',
    anti_sniping_extension_minutes: 5,
    anti_sniping_threshold_minutes: 10,
    payment_deadline_hours: 24,
  });

  useEffect(() => {
    if (isSeller) {
      api.getMyListings('active').then(data => {
        const available = (data || []).filter(l => !l.in_auction);
        setListings(available);
      }).catch(() => {});
    }
  }, [isSeller]);

  const handleListingSelect = (e) => {
    const lid = e.target.value;
    setForm(prev => ({ ...prev, listing_id: lid }));
    const listing = listings.find(l => l.id === lid);
    if (listing) {
      setForm(prev => ({
        ...prev,
        title: listing.title,
        description: listing.description || '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.listing_id) { toast.error('Selecciona un anuncio'); return; }
    if (!form.start_price || parseFloat(form.start_price) <= 0) { toast.error('Precio inicial inválido'); return; }
    if (!form.start_time || !form.end_time) { toast.error('Define las fechas'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        start_price: parseFloat(form.start_price),
        reserve_price: form.reserve_price ? parseFloat(form.reserve_price) : null,
        bid_increment_min: parseFloat(form.bid_increment_min) || 50,
        anti_sniping_extension_minutes: parseInt(form.anti_sniping_extension_minutes) || 5,
        anti_sniping_threshold_minutes: parseInt(form.anti_sniping_threshold_minutes) || 10,
        payment_deadline_hours: parseInt(form.payment_deadline_hours) || 24,
        images: [],
      };

      const result = await api.createAuction(payload);
      toast.success('Subasta creada exitosamente');
      navigate(`/subastas/${result.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const setField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  // Set default start_time to now + 5 min, end_time to now + 3 days
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getTime() + 5 * 60000);
    const end = new Date(now.getTime() + 3 * 86400000);
    const fmt = (d) => d.toISOString().slice(0, 16);
    setForm(prev => ({
      ...prev,
      start_time: fmt(start),
      end_time: fmt(end),
    }));
  }, []);

  return (
    <div className="space-y-6" data-testid="create-auction-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
          <Gavel className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-medium text-zinc-100 font-[Outfit]">Crear Subasta</h1>
          <p className="text-sm text-zinc-500">Subasta un ejemplar para obtener el mejor precio</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {/* Select Listing */}
        <div>
          <label className="text-sm text-zinc-300 block mb-2">Anuncio a subastar *</label>
          <select
            value={form.listing_id}
            onChange={handleListingSelect}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
            data-testid="select-listing"
          >
            <option value="">Selecciona un anuncio</option>
            {listings.map(l => (
              <option key={l.id} value={l.id}>{l.title} — ${l.price?.toLocaleString()} MXN</option>
            ))}
          </select>
          {listings.length === 0 && (
            <p className="text-xs text-zinc-600 mt-1">No tienes anuncios activos disponibles para subastar.</p>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="text-sm text-zinc-300 block mb-2">Título de la subasta</label>
          <input
            type="text"
            value={form.title}
            onChange={setField('title')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
            placeholder="Título descriptivo"
            data-testid="auction-title-input"
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-sm text-zinc-300 block mb-2">Descripción</label>
          <textarea
            value={form.description}
            onChange={setField('description')}
            rows={4}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50 resize-none"
            placeholder="Detalles adicionales de la subasta..."
            data-testid="auction-description-input"
          />
        </div>

        {/* Prices */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-zinc-300 block mb-2">Precio inicial (MXN) *</label>
            <input
              type="number"
              value={form.start_price}
              onChange={setField('start_price')}
              min="1"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
              placeholder="500"
              data-testid="start-price-input"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-300 block mb-2">Precio de reserva (opcional)</label>
            <input
              type="number"
              value={form.reserve_price}
              onChange={setField('reserve_price')}
              min="0"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
              placeholder="Mín. para vender"
              data-testid="reserve-price-input"
            />
          </div>
        </div>

        {/* Bid Increment */}
        <div>
          <label className="text-sm text-zinc-300 block mb-2">Incremento mínimo de puja (MXN)</label>
          <input
            type="number"
            value={form.bid_increment_min}
            onChange={setField('bid_increment_min')}
            min="50"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
            data-testid="bid-increment-input"
          />
        </div>

        {/* Times */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-zinc-300 block mb-2">Inicio *</label>
            <input
              type="datetime-local"
              value={form.start_time}
              onChange={setField('start_time')}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
              data-testid="start-time-input"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-300 block mb-2">Fin *</label>
            <input
              type="datetime-local"
              value={form.end_time}
              onChange={setField('end_time')}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-zinc-100 focus:outline-none focus:border-amber-500/50"
              data-testid="end-time-input"
            />
          </div>
        </div>

        {/* Anti-sniping */}
        <div className="glass-card p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <Shield className="w-4 h-4 text-amber-400" />
            Anti-sniping y configuración
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Extensión (min)</label>
              <input
                type="number"
                value={form.anti_sniping_extension_minutes}
                onChange={setField('anti_sniping_extension_minutes')}
                min="1" max="30"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500/50"
                data-testid="anti-sniping-extension-input"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Umbral (min)</label>
              <input
                type="number"
                value={form.anti_sniping_threshold_minutes}
                onChange={setField('anti_sniping_threshold_minutes')}
                min="1" max="60"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500/50"
                data-testid="anti-sniping-threshold-input"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Plazo pago (h)</label>
              <input
                type="number"
                value={form.payment_deadline_hours}
                onChange={setField('payment_deadline_hours')}
                min="12" max="72"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500/50"
                data-testid="payment-deadline-input"
              />
            </div>
          </div>
          <p className="text-[11px] text-zinc-600">
            Si una puja llega en los últimos {form.anti_sniping_threshold_minutes} minutos, la subasta se extiende {form.anti_sniping_extension_minutes} minutos.
            El ganador tiene {form.payment_deadline_hours} horas para pagar.
          </p>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading || !form.listing_id}
          className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-3"
          data-testid="create-auction-submit"
        >
          <Gavel className="w-5 h-5 mr-2" />
          {loading ? 'Creando subasta...' : 'Crear Subasta'}
        </Button>
      </form>
    </div>
  );
}
